import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, change, SubmissionError, submit } from 'redux-form';

import Button from '../../Button';
import Paragraph from '../../Paragraph';
import Heading from '../../Heading';
import { Row } from '../Scaffold';
import { renderField } from '../../Inputs';
import Progress from '../../Progress';

const aboveZero = (value) => {
  if (value < 0) {
    return 0;
  }
  return value;
};

const required = value => (value || typeof value === 'number' ? undefined : 'Required');
const number = value => (value && isNaN(Number(value)) ? 'Must be a number' : undefined);

const parsers = {
  kgnumber: (value) => {
    let result;
    if (value.indexOf('.') > -1 && value.split('.')[1].length > 2) {
      result = value.substring(0, value.indexOf('.') + 3);
    } else if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = value.substring(0, 3);
    } else if (parseInt(value.split('.')[0], 10) === 0) {
      result = '';
    } else if (parseFloat(value) > 999.99) {
      result = '';
    }

    if (value) {
      return new RegExp(/^(?!0+$)\d{1,3}([.]\d{0,2})?$/g).test(value) ? value : result;
    }
    return '';
  },
  stnumber: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = parseInt(value.substring(0, 3), 10) <= 156 ? value.substring(0, 3) : '';
    }

    if (value) {
      return new RegExp(/^(15[0-6]|1[0-4][0-9]|[1-9]?[0-9])$/g).test(value) ? value : result;
    }
    return '';
  },
  lbnumber: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 4) {
      result = parseInt(value.substring(0, 4), 10) <= 2202 ? value.substring(0, 4) : '';
    }

    if (value) {
      return new RegExp(/^(220[0-2]|2[01][0-9][0-9]|1[0-9]{3}|[1-9]?[0-9]{0,2})$/g).test(value) ? value : result;
    }
    return '';
  },
  cmnumber: (value) => {
    let result;
    if (value.indexOf('.') > -1 && value.split('.')[1].length > 2) {
      result = value.substring(0, value.indexOf('.') + 3);
    } else if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = parseInt(value.substring(0, 3), 10) < 300 ? value.substring(0, 3) : '';
    } else if (parseInt(value.split('.')[0], 10) === 0) {
      result = '';
    } else if (parseFloat(value) > 299.99) {
      result = '';
    }

    if (value) {
      return new RegExp(/^([12]\d{2}|[1-9][0-9]?)([.]\d{0,2})?$$/g).test(value) ? value : result;
    }
    return '';
  },
  ftnumber: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 2) {
      result = parseInt(value.substring(0, 2), 10) <= 10 ? value.substring(0, 2) : '';
    }

    if (value) {
      return new RegExp(/^(10|[0-9])$/g).test(value) ? value : result;
    }
    return '';
  },
  innumber: (value) => {
    let result;
    if (new RegExp(/^\d*$/g).test(value) && !(value.indexOf('.') > -1) && value.length > 3) {
      result = parseInt(value.substring(0, 3), 10) <= 118 ? value.substring(0, 3) : '';
    }

    if (value) {
      return new RegExp(/^(11[0-8]|10[0-9]|[1-9]?[0-9])$/g).test(value) ? value : result;
    }
    return '';
  },
};

class WeightHeight extends React.PureComponent {
  state = {
    showkg: false,
    showcm: false,
    lb: 0,
    st: 0,
    ft: 0,
    in: 0,
  };

  componentWillMount() {
    this.weightChange = this.weightChange.bind(this);
    this.heightChange = this.heightChange.bind(this);
    this.validate = this.validate.bind(this);
  }

  componentDidMount() {
    if (window.pageYOffset > this.sectionRef.offsetTop) {
      window.scrollTo({ top: this.sectionRef.offsetTop, behavior: 'smooth' });
    }

    if (this.props.weight) { this.covertWeight(this.props.weight); }
    if (this.props.height) { this.covertHeight(this.props.height); }
  }

  covertWeight = (kg) => {
    let lb = (kg * 2.204623).toFixed(0);
    const st = Math.floor(lb / 14);
    lb %= 14;
    this.props.dispatch(change('registration', 'weight_stones', st));
    this.props.dispatch(change('registration', 'weight_pounds', lb));

    this.setState({ lb, st });
  };

  covertHeight = (value) => {
    const cm = parseInt(value, 10);
    if (!cm) return;
    let inc = (cm * 0.393700787).toFixed(0);
    const ft = Math.floor(inc / 12);
    inc %= 12;

    this.props.dispatch(change('registration', 'height_ft', ft));
    this.props.dispatch(change('registration', 'height_in', inc));

    this.setState({ ft, in: inc });
  };

  weightChange(e) {
    const weights = {
      st: this.state.st,
      lb: this.state.lb,
    };

    weights.st = this.weightSt.value;
    this.setState({ st: this.weightSt.value });
    weights.lb = this.weightLb.value;
    this.setState({ lb: this.weightLb.value });

    if (e.target.name === 'weight_stones') {
      weights.st = e.target.value;
      this.setState({ st: e.target.value });
    } else if (e.target.name === 'weight_pounds') {
      weights.lb = e.target.value;
      this.setState({ lb: e.target.value });
    }

    weights.st = (weights.st) ? weights.st : 0;
    weights.lb = (weights.lb) ? weights.lb : 0;

    let kg = weights.st * 6.35;
    kg += weights.lb * 0.453;
    kg = kg.toFixed(2);

    this.props.dispatch(change('registration', 'assessment[weight_weight]', kg));
  }

  heightChange(e) {
    const heights = {
      ft: this.state.ft,
      in: this.state.in,
    };

    heights.ft = this.heightFt.value;
    this.setState({ ft: this.heightFt.value });
    heights.in = this.heightIn.value;
    this.setState({ in: this.heightIn.value });

    if (e.target.name === 'height_ft') {
      heights.ft = e.target.value;
      this.setState({ ft: e.target.value });
    } else if (e.target.name === 'height_in') {
      heights.in = e.target.value;
      this.setState({ in: e.target.value });
    }

    heights.ft = (heights.ft) ? heights.ft : 0;
    heights.in = (heights.in) ? heights.in : 0;

    let cm = heights.ft * 30.48;
    cm += heights.in * 2.54;
    cm = cm.toFixed(2);

    this.props.dispatch(change('registration', 'assessment[weight_height]', cm));
  }

  validate(valueSet) {
    const values = valueSet;
    const errors = { assessment: {} };
    let hasError = false;

    if (!values.assessment) values.assessment = {};

    if (!values.assessment.weight_height) {
      errors.assessment.weight_height = 'Please enter your height';
      hasError = true;
    }
    if (values.assessment.weight_height <= 0) {
      errors.assessment.weight_height = 'Your height must be greater than 0';
      hasError = true;
    }
    if (values.assessment.weight_height && values.assessment.weight_height.slice(-1) === '.') {
      errors.assessment.weight_height = 'Please enter a vaild number';
      hasError = true;
    }

    if (!values.assessment.weight_weight) {
      errors.assessment.weight_weight = 'Please enter your weight';
      hasError = true;
    }
    if (values.assessment.weight_weight <= 0) {
      errors.assessment.weight_weight = 'Your weight must be greater than 0';
      hasError = true;
    }
    if (values.assessment.weight_weight && values.assessment.weight_weight.slice(-1) === '.') {
      errors.assessment.weight_weight = 'Please enter a vaild number';
      hasError = true;
    }

    if (!values.weight_stones) {
      errors.weight_stones = 'Please enter your weight';
      hasError = true;
    }
    if (values.weight_stones <= 0) {
      errors.weight_stones = 'Your weight must be greater than 0';
      hasError = true;
    }
    if (!values.height_ft) {
      errors.height_ft = 'Please enter your height';
      hasError = true;
    }
    if (values.height_ft <= 0) {
      errors.height_ft = 'Your height must be greater than 0';
      hasError = true;
    }

    if (hasError) {
      // console.log(hasError, errors);
      throw new SubmissionError(errors);
    }
    // console.log('no errors');
    this.props.dispatch(submit('registration'));
  }

  render() {
    const { previousPage, handleSubmit, submitting } = this.props;
    return (
      <Row classes="lose-weight" setRef={(sectionRef) => { this.sectionRef = sectionRef; }}>
        <Progress step="loseWeight" />
        <Paragraph classes="images" center>
          <img src="https://livewelldorset.s3.amazonaws.com/Pathways/loseweight-roundal.png" alt="" width="100" height="100" />
        </Paragraph>
        <Heading classes="header-text purple" center><span id="register6">Achieve a healthy weight</span></Heading>
        <Paragraph classes="width-limited" center>No matter the change you’d like to make, weight is a useful indicator. It’s helpful to know your starting point so we can tailor your support. We’ll help you work towards your goal and one day, we’ll look back to see how far you’ve come! {"We'll"} match your weight to your height so we can compare against recommended guidelines.</Paragraph>
        <Paragraph classes="width-limited" center>Don’t know your weight? Can you hop on the scales?</Paragraph>

        <form onSubmit={handleSubmit(this.validate)} className="form-register">
          <Paragraph classes="section-text" center><strong>Enter your weight</strong></Paragraph>
          <Paragraph classes="switch-button-container" center>
            <Button classes={['switch-button']} label={this.state.showkg ? 'Switch to imperial' : 'Switch to metric'} onClick={() => this.setState({ showkg: !this.state.showkg })} />
          </Paragraph>
          <div className="input-container">
            {this.state.showkg ? (
              <Field
                name="assessment[weight_weight]"
                type="text"
                component={renderField}
                onChange={(event, kg) => { this.covertWeight(kg); }}
                normalize={aboveZero}
                validate={[number]}
                parse={parsers.kgnumber}
                placeholder="Enter kg"
                classes="text-input-field"
              />
            ) : (
              <div className="input-group">
                <Field
                  ref={(weightSt) => {
                    this.weightSt = weightSt;
                  }}
                  name="weight_stones"
                  type="text"
                  normalize={aboveZero}
                  validate={[number]}
                  parse={parsers.stnumber}
                  component={renderField}
                  onChange={this.weightChange}
                  placeholder="Enter stone"
                  classes="text-input-field"
                />

                <Field
                  ref={(weightLb) => {
                    this.weightLb = weightLb;
                  }}
                  name="weight_pounds"
                  type="text"
                  normalize={aboveZero}
                  validate={[number]}
                  parse={parsers.lbnumber}
                  component={renderField}
                  onChange={this.weightChange}
                  placeholder="Enter pounds"
                  classes="text-input-field"
                />
              </div>
            )}
          </div>
          <Paragraph classes="section-text" center><strong>Enter your height</strong></Paragraph>
          <Paragraph classes="switch-button-container" center>
            <Button classes={['switch-button']} label={this.state.showcm ? 'Switch to imperial' : 'Switch to metric'} onClick={() => this.setState({ showcm: !this.state.showcm })} />
          </Paragraph>
          <div className="input-container">
            {this.state.showcm ? (
              <Field
                name="assessment[weight_height]"
                type="text"
                component={renderField}
                onChange={(event, value) => { this.covertHeight(value); }}
                normalize={aboveZero}
                validate={[number]}
                parse={parsers.cmnumber}
                placeholder="Enter centimetres"
                classes="text-input-field"
              />
            ) : (
              <div className="input-group">
                <Field
                  ref={(heightFt) => {
                    this.heightFt = heightFt;
                  }}
                  name="height_ft"
                  type="text"
                  normalize={aboveZero}
                  validate={[number]}
                  parse={parsers.ftnumber}
                  component={renderField}
                  onChange={this.heightChange}
                  placeholder="Enter foot"
                  classes="text-input-field"
                />

                <Field
                  ref={(heightIn) => {
                    this.heightIn = heightIn;
                  }}
                  name="height_in"
                  type="text"
                  normalize={aboveZero}
                  validate={[number]}
                  parse={parsers.innumber}
                  component={renderField}
                  onChange={this.heightChange}
                  placeholder="Enter inch"
                  classes="text-input-field"
                />
              </div>
            )}
          </div>
          <Paragraph classes="section-text" center><strong>Would you like help to achieve a healthy weight?</strong></Paragraph>
          <Field name="pathway_weight" validate={[required]} type="radiogroup" params={{ dataKey: 'LeaveMessage', classes: 'purple textUppercase' }} component={renderField} centered />
          <Paragraph classes="button-groups divideline" center>
            <button className="button js-register-advance color-button purple" type="submit" disabled={submitting}>Next</button>
            <Button classes={['underline-button']} label="Back" onClick={previousPage} />
          </Paragraph>
        </form>
      </Row>
    );
  }
}

WeightHeight.propTypes = {
  previousPage: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default reduxForm({
  form: 'registration', // <------ same form name
  destroyOnUnmount: false, // <------ preserve form data
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
  // validate,
})(WeightHeight);



// WEBPACK FOOTER //
// ./src/Components/Register/FormPages/WeightHeight.jsx